<template>
  <div class="consultoria-servicio" :class="{ 'tab-fade': isAnimating }">
    <!-- HERO -->
    <section class="hero-hero d-flex align-items-center justify-content-center" data-aos="fade-up" data-aos-duration="1200">
      <div class="hero-bg-img" :style="`background-image:url('${data.hero?.image}')`"></div>
      <div class="hero-overlay"></div>

      <!-- Breadcrumb en overlay -->
      <nav
        class="hero-crumbs hero-crumbs--light"
        role="navigation"
        aria-label="breadcrumbs"
      >
        <router-link
          class="crumb-link"
          to="/servicios"
          aria-label="Volver a Servicios"
          :title="crumbServiciosLabel"
        >
          <svg class="crumb-icon" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
            <path fill="currentColor" d="M9.78 3.22a.75.75 0 0 1 0 1.06L6.06 8l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0z"/>
          </svg>
          <span class="crumb-text">{{ crumbServiciosLabel }}</span>
        </router-link>
        <span class="crumb-sep" aria-hidden="true">/</span>
        <span class="crumb-current" aria-current="page">{{ data.hero?.title }}</span>
      </nav>

      <div class="hero-hero-content text-center text-dark">
        <h1 class="display-5 fw-bold hero-hero-title">{{ data.hero?.title }}</h1>
      </div>
    </section>

    <!-- TABS SUPERIORES -->
    <div class="consultoria-tabs-top d-flex justify-content-center" data-aos="fade-up" data-aos-duration="1200">
      <div
        v-for="(tab, idx) in tabs"
        :key="tab.title"
        class="consultoria-tab"
        :class="{ active: tabIndex === idx }"
        @click="changeTab(idx)"
      >
        {{ tab.title }}
      </div>
    </div>

    <div class="container-xxl consultoria-body px-1 px-sm-3 pt-4 pb-1">
      <div class="row gx-lg-5">
        <!-- SIDEBAR DESKTOP -->
        <nav class="col-lg-2 d-none d-lg-flex flex-column align-items-start sticky-top" style="top:90px;z-index:20;" data-aos="fade-up" data-aos-duration="1200">
          <ul class="sidebar-nav">
            <li v-for="item in selectedTab.index" :key="item.anchor">
              <a
                :href="'#'+item.anchor"
                :class="['sidebar-link', {active: activeSection === item.anchor}]"
                @click.prevent="scrollToAnchor(item.anchor)"
              >{{ item.label }}</a>
            </li>
          </ul>
        </nav>

        <!-- NAV HORIZONTAL MOBILE -->
        <div class="col-12 d-lg-none mb-3">
          <div class="mobile-nav px-2 py-1 d-flex flex-wrap gap-2 justify-content-center" data-aos="fade-up" data-aos-duration="1200">
            <a
              v-for="item in selectedTab.index"
              :key="item.anchor"
              :href="'#'+item.anchor"
              class="mobile-nav-link"
              :class="{active: activeSection === item.anchor}"
              @click.prevent="scrollToAnchor(item.anchor)"
            >{{ item.label }}</a>
          </div>
        </div>

        <!-- MAIN CONTENT -->
        <main class="col-lg-10 px-lg-4">
          <!-- INTRODUCCIÓN -->
          <section :id="selectedTab.index[0]?.anchor" class="mb-4 intro-section" data-aos="fade-up" data-aos-duration="1200">
            <div class="row align-items-center">
              <div class="col-md-5 text-center mb-2 mb-md-0" data-aos="fade-up" data-aos-duration="1200">
                <img :src="tabDetailImage" :alt="selectedTab.title" class="hero-img img-fluid shadow" />
              </div>
              <div class="col-md-7" data-aos="fade-up" data-aos-duration="1200">
                <h2 class="section-title pt-2 pb-1">{{ selectedTab.title }}</h2>
                <strong class="text-primary d-block mb-1" style="font-size:1.01rem;">{{ selectedTab.index[0]?.label }}</strong>
                <p class="hero-intro-text" v-html="selectedTab.introduccion?.content"></p>
              </div>
            </div>
          </section>
          <hr />

          <!-- ¿Qué incluye el servicio? -->
          <section :id="selectedTab.index[1]?.anchor" class="mb-5">
            <h3 class="section-title mb-2" data-aos="fade-up" data-aos-duration="1200">{{ selectedTab.index[1]?.label }}</h3>
            <div class="row d-flex justify-content-end">
              <div class="col-lg-10">
                <div v-if="selectedTab.incluye?.description" class="mt-2 small text-muted ps-1">
                  {{ selectedTab.incluye.description }}
                </div>
                <div class="accordion shadow-sm" id="includesAccordion">
                  <div
                    data-aos="fade-up" data-aos-duration="1200"
                    class="accordion-item"
                    v-for="(item, i) in selectedTab.incluye?.items"
                    :key="'inc-' + i"
                  >
                    <h2 class="accordion-header" :id="'inc-heading'+i">
                      <button
                        class="accordion-button fw-bold"
                        type="button"
                        :class="{ collapsed: activeAccordion !== i }"
                        @click="toggleAccordion(i)"
                      >
                        {{ item.title }}
                      </button>
                    </h2>
                    <div
                      :id="'inc-collapse'+i"
                      class="accordion-collapse collapse"
                      :class="{ show: activeAccordion === i }"
                      :aria-labelledby="'inc-heading'+i"
                      data-bs-parent="#includesAccordion"
                    >
                      <div class="accordion-body">
                        <span>{{ item.content }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <hr />

          <!-- Ventajas del Servicio -->
          <section :id="selectedTab.index[2]?.anchor" class="mb-5">
            <h3 class="section-title mb-3" data-aos="fade-up" data-aos-duration="1200">{{ selectedTab.index[2]?.label }}</h3>
            <ul class="advantages-list list-unstyled mt-2">
              <li v-for="(item, i) in selectedTab.ventajas?.items" :key="'adv-' + i" data-aos="fade-up" data-aos-duration="1200">
                <span class="check-icon">
                  <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#3d7ecb" d="M7.4 14.3l-4.5-4.6 1.1-1.1 3.4 3.5 6.1-6.1 1.1 1.1z"/></svg>
                </span>
                {{ item }}
              </li>
            </ul>
          </section>
          <hr />

          <!-- Para quién es este servicio -->
          <section :id="selectedTab.index[3]?.anchor" class="mb-5" data-aos="fade-up" data-aos-duration="1200">
            <h3 class="section-title mb-4">{{ selectedTab.index[3]?.label }}</h3>
            <div v-if="selectedTab.paraQuien?.description" class="mb-3 para-desc" style="font-size:.97em">{{ selectedTab.paraQuien.description }}</div>
            <div class="row g-3">
              <div
                data-aos="fade-up" data-aos-duration="1200"
                class="col-md-6 col-lg-6"
                v-for="(perfil, i) in selectedTab.paraQuien?.perfiles"
                :key="'perfil-'+i"
              >
                <div class="perfil-card perfil-card shadow-sm h-100">
                  <span class="perfil-desc" data-aos="fade-up" data-aos-duration="1200">{{ perfil }}</span>
                </div>
              </div>
            </div>
          </section>
          <hr />

          <!-- Banner Minimalista Black -->
          <section class="banner-minimal my-5 py-4" data-aos="fade-up" data-aos-duration="1200">
            <div class="container-fluid">
              <div class="row align-items-center justify-content-center">
                <div class="col-lg-8 col-md-7 py-2 px-3">
                  <div class="banner-minimal-title mb-2" data-aos="fade-up" data-aos-duration="1200">
                    {{ data.banner?.content?.split('\n')[0] }}
                  </div>
                  <div class="banner-minimal-desc mb-3">
                    <span v-html="bannerLines"></span>
                  </div>
                  <button
                    data-aos="fade-up" data-aos-duration="1200"
                    class="btn btn-light rounded-pill px-4 py-2 mt-1 shadow-sm banner-minimal-btn"
                    @click="goToWhatsApp"
                  >
                    ¡Hablemos!
                  </button>
                </div>
                <div class="col-lg-4 col-md-5 text-center d-none d-md-block">
                  <img
                    data-aos="fade-up" data-aos-duration="1200"
                    :src="data.banner?.image"
                    :alt="selectedTab.title"
                    class="banner-minimal-img img-fluid"
                    style="max-width:180px;"
                  />
                </div>
              </div>
            </div>
          </section>

          <!-- CTA -->
          <section class="cta-section text-center mt-3 mb-5" data-aos="fade-up" data-aos-duration="1200">
            <div class="cta-inner">
              <h4 class="cta-title fw-bold mb-2" data-aos="fade-up" data-aos-duration="1200">
                <i>{{ data.cta?.title }}</i>
              </h4>
              <div class="cta-content mb-3" data-aos="fade-up" data-aos-duration="1200">{{ data.cta?.content }}</div>
              <button
                data-aos="fade-up" data-aos-duration="1200"
                class="btn btn-sec btn-primary px-4 py-2"
                @click="goToContacto"
                style="border-radius: 25px; font-weight:600; font-size:1rem;">
                {{ data.cta?.button }}
              </button>
            </div>
          </section>
        </main>
      </div>
    </div>
    <!-- FOOTER GLOBAL -->
    <SiteFooter :selectedLang="selectedLang" />
  </div>
</template>

<script>
import { useSEO } from '@/composables/useSEO';
import { servicios } from '@/assets/data/data.ts'
import SiteFooter from '@/components/SiteFooter.vue'
import AOS from 'aos'
import 'aos/dist/aos.css'

export default {
  name: 'ConsultoriaView',
  components: {
    SiteFooter
  },
  props: {
    selectedLang: {
      type: String,
      required: true
    }
  },
  setup() {
    useSEO({
      title: 'Consultoría y Asesoría de Ingeniería',
      description: 'Consultoría especializada en automatización, Industria 4.0 y transformación digital. Acompañamos tu proyecto industrial desde el diagnóstico hasta la implementación.',
      path: '/consultoria-asesoria-ingenieria',
    });
  },
  data() {
    return {
      tabIndex: 0,
      activeSection: "introduccion",
      activeAccordion: 0,
      isAnimating: false
    }
  },
  computed: {
    data() {
      return servicios['consultoria']?.detail?.[this.selectedLang] || servicios['consultoria']?.detail?.es || {};
    },
    tabs() {
      return this.data.tabs || [];
    },
    selectedTab() {
      return this.tabs[this.tabIndex] || { index: [] };
    },
    tabDetailImage() {
      const detalleImages = servicios['consultoria']?.images?.detalle?.[this.selectedLang];
      if (Array.isArray(detalleImages) && typeof this.tabIndex !== "undefined" && detalleImages[this.tabIndex]) {
        return detalleImages[this.tabIndex];
      }
      return this.selectedTab.image || '';
    },
    bannerLines() {
      if (!this.data.banner?.content) return '';
      return this.data.banner.content.split('\n').slice(1).join('<br>');
    },
    /* i18n para “Servicios” en breadcrumb */
    crumbServiciosLabel() {
      const map = { es: 'Servicios', en: 'Services', de: 'Leistungen' };
      return map[this.selectedLang] || map.es;
    }
  },
  mounted() {
    window.addEventListener('scroll', this.onScrollSpy);
    AOS.init({ duration: 800, once: true });
    setTimeout(() => AOS.refresh(), 300);
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.onScrollSpy);
  },
  methods: {
    changeTab(idx) {
      if (this.tabIndex === idx) return;
      this.isAnimating = true;
      this.tabIndex = idx;
      this.activeSection = "introduccion";
      this.activeAccordion = 0;
      setTimeout(() => { this.isAnimating = false; }, 700);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    scrollToAnchor(anchor) {
      const el = document.getElementById(anchor);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    toggleAccordion(i) {
      this.activeAccordion = this.activeAccordion === i ? null : i;
    },
    goToContacto() {
      this.$router.push({ path: '/contacto' });
    },
    goToWhatsApp() {
      const message = encodeURIComponent("¡Hola! Estoy interesado en consultoría y asesoría para mi empresa 👋");
      const waUrl = `https://wa.me/525539960462?text=${message}`;
      window.open(waUrl, '_blank');
    },
    onScrollSpy() {
      let found = "introduccion";
      for (const section of (this.selectedTab?.index || [])) {
        const el = document.getElementById(section.anchor);
        if (el && window.scrollY + 130 >= el.offsetTop) found = section.anchor;
      }
      this.activeSection = found;
    }
  }
}
</script>

<style scoped>
.consultoria-servicio {
  font-family: 'JetBrains Mono', monospace;
  background: #fafcff;
  color: #1b2230;
  min-height: 100vh;
}
.hero-hero {
  height: 240px;
  min-height: 175px;
  position: relative;
  margin-bottom: 1.6rem;
  background: #fff;
}
.hero-bg-img {
  position: absolute;
  z-index: 1;
  inset: 0;
  background-size: cover;
  background-position: center;
  filter: brightness(0.75) blur(0.6px);
  transition: background-image 0.6s ease-in-out;
}
.hero-overlay {
  position: absolute;
  z-index: 2;
  inset: 0;
  background: linear-gradient(90deg, #fff9 10%, #e6e8ea80 100%);
}

/* Breadcrumbs en overlay (versión clara) */
.hero-crumbs {
  position: absolute;
  z-index: 4;
  top: 12px;
  left: 14px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  line-height: 1;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.hero-crumbs--light {
  background: rgba(255, 255, 255, 0.86);
  border: 1px solid rgba(0, 0, 0, 0.08);
  color: #0b2342;
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
}
.crumb-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
  color: inherit;
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.crumb-link:hover { opacity: 0.9; transform: translateX(-1px); }
.crumb-link:focus { outline: 2px solid #0b2342; outline-offset: 2px; }
.crumb-icon { display: inline-block; }
.crumb-sep { opacity: 0.65; margin: 0 2px; }
.crumb-current { opacity: 0.9; font-weight: 600; }

.hero-hero-content {
  z-index: 3;
  position: relative;
  width: 100%;
  padding-top: 0.8em;
}
.hero-hero-title {
  font-family: 'JetBrains Mono', monospace;
  letter-spacing: 0.04em;
  font-size: 2.4rem;
  color: #202026;
}

.consultoria-tabs-top {
  border-bottom: 2px solid #222;
  background: #fff;
}
.consultoria-tab {
  cursor: pointer;
  padding: 1.05em 2.2em .7em 2.2em;
  border: 2px solid #222;
  border-bottom: none;
  border-radius: 16px 16px 0 0;
  font-size: 1.09rem;
  background: #fff;
  margin: 0 5px -2px 0;
  font-family: inherit;
  transition: background 0.14s, border-color 0.13s;
  font-weight: 500;
}
.consultoria-tab.active {
  background: #fafcff;
  font-weight: 700;
  border-bottom: 2px solid #fafcff;
}

.sidebar-nav {
  position: sticky;
  top: 100px;
  padding: 0;
  list-style: none;
}
.sidebar-link {
  display: block;
  font-weight: bold;
  color: #2c4366;
  margin-bottom: 0.8rem;
  font-size: 1.01rem;
  border-left: 3px solid transparent;
  padding: 0.16rem 0.7rem 0.16rem 0.65rem;
  border-radius: 3px;
  background: transparent;
  transition: background 0.17s, border-color 0.18s;
}
.sidebar-link.active,
.sidebar-link:hover {
  background: #ececf4;
  color: #1565c0;
  border-left: 3px solid #3d7ecb;
}

.mobile-nav {
  background: #f3f5fb;
  border-radius: 9px;
  font-family: 'JetBrains Mono', monospace;
  font-weight: 700;
}
.mobile-nav-link {
  padding: 0.4em 0.9em;
  color: #28568a;
  text-decoration: none;
  border-radius: 6px;
  font-weight: 600;
  background: transparent;
  font-size: 1rem;
  transition: background .19s, color .13s;
}
.mobile-nav-link.active,
.mobile-nav-link:hover {
  background: #e2e9fa;
  color: #00336d;
}

.section-title {
  font-family: 'JetBrains Mono', monospace;
  font-size: 1.22rem;
  color: #28568a;
  font-weight: 700;
  letter-spacing: 0.03em;
  margin-bottom: 1.1rem;
}
.hero-img {
  border-radius: 12px;
  box-shadow: 0 6px 32px rgba(32,40,80,0.13);
  width: 96%;
  max-width: 320px;
  margin: 0 auto;
  display: block;
  background: #fff;
  border: 1.6px solid #bbb;
  transition: transform 0.4s ease, box-shadow 0.4s ease, filter 0.4s ease, opacity 0.4s ease;
}
.hero-img:hover {
  transform: scale(1.05);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3);
  cursor: pointer;
}
.hero-intro-text {
  font-size: 1.01rem;
  color: #21285c;
  margin-top: 0.3rem;
  letter-spacing: 0.01em;
  line-height: 1.32;
  font-family: inherit;
}
.advantages-list li {
  display: flex;
  align-items: flex-start;
  gap: 0.7em;
  margin-bottom: 0.7em;
  font-size: 1.01rem;
}
.check-icon { flex-shrink: 0; margin-top: 1px; }

.perfil-card {
  background: #fff;
  border: 1.5px solid #222;
  border-radius: 11px;
  padding: 1.07em 1.1em 1em 1.1em;
  min-height: 100px;
  font-size: 0.99rem;
  font-family: inherit;
  display: flex;
  align-items: flex-start;
  transition: box-shadow 0.2s;
  transition: transform 0.4s ease, box-shadow 0.4s ease, filter 0.4s ease, opacity 0.4s ease;
}
.perfil-card:hover {
  box-shadow: 0 3px 20px rgba(72,136,200,0.06);
  transform: scale(1.05);
}
.perfil-desc { font-family: inherit; font-weight: 500; color: #1b2230; font-size: 1em; }

.banner-minimal {
  background: #181818;
  border-radius: 15px;
  border: 2px solid #1b1b1b;
  color: #fff;
  font-family: inherit;
  margin-bottom: 1.5em;
  letter-spacing: 0.02em;
  position: relative;
}
.banner-minimal-title { font-size: 1.29rem; font-family: inherit; font-weight: bold; margin-bottom: 0.38em; line-height: 1.22; }
.banner-minimal-desc { font-size: 1.01rem; color: #eee; margin-bottom: 1rem; line-height: 1.45; }
.banner-minimal-btn {
  font-family: inherit;
  border: 2px solid #fff;
  color: #181818;
  background: #fff;
  font-size: 1.05rem;
  font-weight: 700;
  transition: background .15s, color .15s, border .13s;
}
.banner-minimal-btn:hover { background: #ffb24c; color: #181818; border-color: #ffb24c; }
.banner-minimal-img {
  border-radius: 11px; box-shadow: 0 2px 14px #0003; margin: 0 auto; background: #f4f4f4; border: none;
  object-fit: cover; transition: transform 0.4s ease, box-shadow 0.4s ease, filter 0.4s ease, opacity 0.4s ease;
}
.banner-minimal-img:hover { transform: scale(1.05); box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3); cursor: pointer; }

.btn-sec{ background-color: #2c74b8 !important; }

.cta-section{
  background: #eef5fc; border-radius: 22px; max-width: 700px; margin: 0 auto;
  padding: 2.3rem 2.1rem 2.7rem 2.1rem; box-shadow: 0 3px 24px rgba(44,99,171,0.13); border: 2px solid #dbe9fa;
}
.cta-section, .btn-sec{ transition: transform 0.4s ease, box-shadow 0.4s ease, filter 0.4s ease, opacity 0.4s ease; }
.cta-section:hover, .btn-sec:hover{ transform: scale(1.05); box-shadow: 0 10px 20px rgba(0, 0, 0, 0.3); cursor: pointer; }
.cta-title { font-size: 1.35rem; color: #28568a; letter-spacing: 0.04em; font-family: 'JetBrains Mono', monospace; }
.cta-content { font-size: 1.09em; margin-bottom: 1.2em; }
.cta-inner .btn { background: #181818; color: #fff; border-radius: 25px; font-size: 1.1em; font-family: inherit; font-weight: 600; border: none; }
.cta-inner .btn:hover { background: #0d46a5; }

@keyframes fadeTab {
  0% { opacity: 0.5; transform: scale(0.97); }
  100% { opacity: 1; transform: scale(1); }
}
.tab-fade { animation: fadeTab 0.6s ease-in-out; }

@media (max-width: 991px) {
  .sidebar-nav { display: none; }
  .hero-img { max-width: 220px;}
  .banner-minimal-img { max-width: 110px; }
}
@media (max-width: 767px) {
  .hero-hero { height: 120px; min-height: 70px; }
  .hero-hero-title { font-size: 1.17rem; }
  .hero-img { max-width: 90vw;}
  .banner-minimal-img { max-width: 97vw; }
  .perfil-card { min-height: unset; font-size: .96rem; }
  .banner-minimal-title { font-size: 1.1rem;}
  .hero-crumbs { top: 8px; left: 10px; padding: 5px 10px; font-size: 0.85rem; gap: 6px; }
}
@media (max-width: 520px) {
  .hero-img, .banner-minimal-img { max-width: 99vw; }
}
</style>
